import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Chip,
} from "@mui/material";

const getVal = (obj, keys, fallback = "") => {
  for (const key of keys) {
    if (String(key).includes(".")) {
      const value = String(key)
        .split(".")
        .reduce(
          (acc, part) =>
            acc !== undefined && acc !== null ? acc[part] : undefined,
          obj,
        );
      if (value !== undefined && value !== null) return value;
      continue;
    }
    if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
  }
  return fallback;
};

const getTypeNameFromObj = (obj, fallback = "") =>
  getVal(obj, ["type.name", "type_name", "typeName"], fallback);

const getIntervalLabelFromObj = (obj) => {
  const nInterval = Number(
    getVal(obj, ["n_interval", "nInterval", "n_price_validity_interval"], 0),
  );
  const interval = String(
    getVal(obj, ["interval", "price_validity_interval", "interval_type"], ""),
  ).trim();
  if (!nInterval && !interval) return "";
  if (!nInterval) return interval;
  if (!interval) return `${nInterval}`;
  return `${nInterval} ${interval}`;
};

const normalizeUnits = (units) =>
  (Array.isArray(units) ? units : [])
    .map((u) => ({
      ...u,
      id: Number(getVal(u, ["id", "unit_id"], 0)),
      start_range: Number(getVal(u, ["start_range", "StartRange"], 0)),
      end_range: Number(getVal(u, ["end_range", "EndRange"], 0)),
      marketplace_price: Number(
        getVal(u, ["marketplace_price", "Marketplace_price"], 0),
      ),
      vendor_id: Number(getVal(u, ["vendor_id", "vendorId", "vendor.id"], 0)),
      vendor_name: getVal(u, ["vendor_name", "vendorName", "vendor.name"], ""),
    }))
    .sort((a, b) => a.start_range - b.start_range);

const findRangeIndex = (units, quantity) =>
  units.findIndex((u) => quantity >= u.start_range && quantity <= u.end_range);

const getUnitRangeBounds = (units) => {
  const normalized = normalizeUnits(units);
  if (!normalized.length) return { min: 0, max: 0 };
  return {
    min: Number(normalized[0]?.start_range || 0),
    max: Number(normalized[normalized.length - 1]?.end_range || 0),
  };
};

const buildRowsByQuantity = (units, unitPriceType, quantity) => {
  const sorted = normalizeUnits(units);
  if (!sorted.length || !quantity) return [];
  const ri = findRangeIndex(sorted, quantity);
  if (ri === -1) return [];

  const isPartial = String(unitPriceType || "").toLowerCase() === "partial";
  const base = isPartial ? sorted.filter((_, i) => i <= ri) : [sorted[ri]];

  return base.map((item, i) => {
    const rowQty = isPartial
      ? i === ri
        ? quantity - item.start_range + 1
        : item.end_range - item.start_range + 1
      : quantity;
    const total = Number(rowQty * Number(item.marketplace_price || 0)).toFixed(
      2,
    );
    return {
      ...item,
      quantity: rowQty,
      total,
    };
  });
};

const overlayMarketplaceFromSubscription = (dbUnits, subscriptionUnits) => {
  const db = normalizeUnits(dbUnits);
  const sub = normalizeUnits(subscriptionUnits);
  if (!db.length || !sub.length) return db;
  return db.map((row) => {
    const byId = sub.find((s) => Number(s.id) === Number(row.id));
    const byRange = sub.find(
      (s) =>
        Number(s.start_range) === Number(row.start_range) &&
        Number(s.end_range) === Number(row.end_range),
    );
    const match = byId || byRange;
    if (!match) return row;
    return {
      ...row,
      marketplace_price: Number(match.marketplace_price || 0),
      vendor_price: Number(match.vendor_price || 0),
    };
  });
};

const buildUnitSignature = (units) =>
  normalizeUnits(units)
    .map((u) => ({
      id: Number(u.id || 0),
      start_range: Number(u.start_range || 0),
      end_range: Number(u.end_range || 0),
      quantity: Number(u.quantity || 0),
      marketplace_price: Number(u.marketplace_price || 0),
    }))
    .sort((a, b) =>
      a.start_range === b.start_range
        ? a.end_range - b.end_range || a.id - b.id
        : a.start_range - b.start_range,
    );

const formatDateLabel = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const normalizeApiPrices = (data) => {
  const list = Array.isArray(data?.data)
    ? data.data
    : Array.isArray(data?.prices)
      ? data.prices
      : [];

  const byPrice = new Map();
  list.forEach((entry) => {
    const priceId = Number(getVal(entry, ["price_id", "id"], 0));
    if (!priceId) return;
    if (!byPrice.has(priceId)) {
      byPrice.set(priceId, {
        price_id: priceId,
        price_name: getVal(
          entry,
          ["name", "price_name", "title"],
          `Price ${priceId}`,
        ),
        type_name: getTypeNameFromObj(entry),
        interval_label: getIntervalLabelFromObj(entry),
        unit_price_type: getVal(
          entry,
          ["unit_price_type", "unitPriceType"],
          "Partial",
        ),
        vendorGroups: [],
      });
    }
    const priceObj = byPrice.get(priceId);

    const explicitGroups = Array.isArray(entry?.vendor_groups)
      ? entry.vendor_groups
      : Array.isArray(entry?.vendors)
        ? entry.vendors.map((v) => ({
            vendor_id: getVal(v, ["vendor_id", "vendorId", "id"], 0),
            vendor_name: getVal(
              v,
              ["vendor_name", "vendorName", "name"],
              "Vendor",
            ),
            unit_ids: Array.isArray(v?.unit_ids)
              ? v.unit_ids
              : Array.isArray(v?.unit_prices)
                ? v.unit_prices
                : v?.units || [],
          }))
        : [];

    if (explicitGroups.length) {
      explicitGroups.forEach((group) => {
        const vendorId = Number(
          getVal(group, ["vendor_id", "vendorId", "id", "vendor.id"], 0),
        );
        const unitIds = normalizeUnits(
          getVal(group, ["unit_ids", "unit_prices", "units"], []),
        );
        if ((!vendorId && vendorId !== -1) || !unitIds.length) return;
        priceObj.vendorGroups.push({
          vendor_id: vendorId,
          vendor_name: getVal(
            group,
            ["vendor_name", "vendorName", "name", "vendor.name"],
            vendorId === -1 ? "Admin-owned" : `Vendor ${vendorId}`,
          ),
          unit_ids: unitIds,
        });
      });
      return;
    }

    const flatUnits = normalizeUnits(
      getVal(entry, ["unit_ids", "unit_prices", "units"], []),
    );
    if (!flatUnits.length) return;
    const vendorMap = new Map();
    flatUnits.forEach((u) => {
      const vendorId = Number(getVal(u, ["vendor_id", "vendorId"], 0));
      if (!vendorId && vendorId !== -1) return;
      if (!vendorMap.has(vendorId)) {
        vendorMap.set(vendorId, {
          vendor_id: vendorId,
          vendor_name:
            getVal(u, ["vendor_name", "vendorName"], "") ||
            (vendorId === -1 ? "Admin-owned" : `Vendor ${vendorId}`),
          unit_ids: [],
        });
      }
      vendorMap.get(vendorId).unit_ids.push(u);
    });
    vendorMap.forEach((v) => priceObj.vendorGroups.push(v));
  });

  return Array.from(byPrice.values()).map((p) => ({
    ...p,
    vendorGroups: p.vendorGroups.filter((g) => g.unit_ids.length > 0),
  }));
};

const AdminUpdateSubscriptionDrawer = ({
  open,
  onClose,
  subscription,
  onUpdated,
}) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [priceOptions, setPriceOptions] = useState([]);
  const [selectedPriceId, setSelectedPriceId] = useState("");
  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [currentPriceRemoved, setCurrentPriceRemoved] = useState(false);
  const [quantity, setQuantity] = useState(
    Number(getVal(subscription, ["noOfLicences", "quantity"], 1)),
  );
  const [renderedRows, setRenderedRows] = useState([]);
  const [quantityRangeError, setQuantityRangeError] = useState("");
  const [showApplyDialog, setShowApplyDialog] = useState(false);
  const [applyWhen, setApplyWhen] = useState("immediate");
  // Default the custom date to a week out so the picker isn't empty.
  const [customDate, setCustomDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  });
  const [pendingPayload, setPendingPayload] = useState(null);
  // vendor_price is our internal cost (what we pay the vendor), not what
  // the customer is billed. Editing it never touches Stripe. It has its own
  // save button, and Save Update carries the same edits (withVendorEdits).
  // Keyed by unit_id; value is the edited string (empty = unchanged).
  const [vendorPriceEdits, setVendorPriceEdits] = useState({});
  const [savingVendorPrices, setSavingVendorPrices] = useState(false);
  const [vendorPriceError, setVendorPriceError] = useState("");
  const [vendorPriceSuccess, setVendorPriceSuccess] = useState("");
  const [schedule, setSchedule] = useState(null);
  // The coupon live on the subscription right now. Saving a price change
  // clears it unless one is entered below, so the admin has to see it
  // before they save — not discover it on the next invoice.
  const [currentDiscount, setCurrentDiscount] = useState(null);
  // Seat changes held for Ingram (vendor-first). They have no Stripe schedule
  // until Ingram confirms, so the schedule block above never shows them.
  const [pendingSeatChanges, setPendingSeatChanges] = useState([]);
  const [cancelingSchedule, setCancelingSchedule] = useState(false);
  const [couponInput, setCouponInput] = useState("");
  const [couponChecking, setCouponChecking] = useState(false);
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponError, setCouponError] = useState("");
  // Price pinning: a pinned sub keeps its custom price and is excluded from
  // catalog price rollouts. `pin` is the checkbox for THIS update; it
  // auto-checks when a custom (off-catalog) price is detected, unless the
  // admin has manually toggled it (pinTouched). `isPinned` reflects the
  // sub's CURRENT persisted pin state (from the row) for the badge + Unpin.
  const [pin, setPin] = useState(false);
  const [pinNote, setPinNote] = useState("");
  const [pinTouched, setPinTouched] = useState(false);
  const [isPinned, setIsPinned] = useState(false);
  const [unpinning, setUnpinning] = useState(false);
  const subscriptionUnitRows = useMemo(
    () => normalizeUnits(getVal(subscription, ["unit_ids"], [])),
    [subscription],
  );
  const dueDate = getVal(
    subscription,
    [
      "dueDate",
      "due_date",
      "renewal_date",
      "next_billing_date",
      "nextBillingDate",
    ],
    "",
  );
  const dueDateLabel = formatDateLabel(dueDate);
  const currentQuantity = Number(
    getVal(subscription, ["noOfLicences", "quantity"], 1),
  );

  const currentPriceId = Number(
    getVal(subscription, ["price_id", "priceId"], 0),
  );
  const currentUnitPriceType = getVal(
    subscription,
    ["unit_price_type", "unitPriceType"],
    "Partial",
  );
  const currentVendorId = Number(
    getVal(subscriptionUnitRows?.[0], ["vendor_id", "vendorId"], 0),
  );
  const currentVendorName = getVal(
    subscriptionUnitRows?.[0],
    ["vendor_name", "vendorName"],
    currentVendorId === -1 ? "Admin-owned" : `Vendor ${currentVendorId}`,
  );

  const allPriceOptions = useMemo(() => {
    const list = Array.isArray(priceOptions) ? [...priceOptions] : [];
    if (currentPriceRemoved) {
      return list.filter((p) => Number(p.price_id) !== Number(currentPriceId));
    }
    return list;
  }, [priceOptions, currentPriceRemoved, currentPriceId]);

  const currentUnitSetKey = useMemo(() => {
    const ids = subscriptionUnitRows.map((u) => u.id).sort((a, b) => a - b);
    return ids.join(",");
  }, [subscriptionUnitRows]);

  useEffect(() => {
    if (!open) return;
    setCurrentPriceRemoved(false);
    setSelectedPriceId(String(currentPriceId || ""));
    setSelectedVendorId(String(currentVendorId || ""));
    setQuantity(Number(getVal(subscription, ["noOfLicences", "quantity"], 1)));
    setVendorPriceEdits({});
    setVendorPriceError("");
    setVendorPriceSuccess("");
    setCouponInput("");
    setCouponInfo(null);
    setCouponError("");
    setPin(false);
    setPinNote("");
    setPinTouched(false);
    setIsPinned(Boolean(getVal(subscription, ["price_pinned", "pricePinned"], false)));
    // Pull the LIVE Stripe schedule so we can surface any pending change
    // (instead of silently editing on top of stale DB pricing).
    setSchedule(null);
    setCurrentDiscount(null);
    setPendingSeatChanges([]);
    (async () => {
      try {
        const sid = Number(getVal(subscription, ["id"], 0));
        if (!sid) return;
        const r = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/admin/subscriptions/${sid}/schedule`,
          { credentials: "include" },
        );
        const j = await r.json();
        if (j?.data?.hasSchedule) setSchedule(j.data);
        // Set regardless of hasSchedule — a sub with no pending phase
        // still has a live discount that this update will clear.
        setCurrentDiscount(j?.data?.currentDiscount || null);
        setPendingSeatChanges(
          Array.isArray(j?.data?.pendingSeatChanges) ? j.data.pendingSeatChanges : [],
        );
      } catch (_) {
        /* non-fatal */
      }
    })();
    const fetchPrices = async () => {
      setLoading(true);
      setError("");
      setSuccess("");
      try {
        const resolvedPriceId = Number(
          getVal(subscription, ["price_id", "priceId"], 0),
        );
        if (!resolvedPriceId || Number.isNaN(resolvedPriceId)) {
          throw new Error("Invalid price id for subscription.");
        }
        const response = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/admin/get_active_prices_by_template`,
          {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              priceId: resolvedPriceId,
            }),
          },
        );
        const data = await response.json();
        if (!response.ok || !data?.success) {
          throw new Error(data?.message || "Unable to load price options");
        }
        const normalizedAll = normalizeApiPrices(data);
        const currentFromApi = normalizedAll.find(
          (p) => Number(p.price_id) === Number(currentPriceId),
        );
        if (!currentFromApi && currentPriceId) {
          normalizedAll.unshift({
            price_id: currentPriceId,
            price_name: `${getVal(subscription, ["marketplaceName", "name"], "Current Price")} (Current)`,
            type_name: getTypeNameFromObj(subscription),
            interval_label: getIntervalLabelFromObj(subscription),
            unit_price_type: currentUnitPriceType,
            vendorGroups: [
              {
                vendor_id: currentVendorId,
                vendor_name: currentVendorName,
                unit_ids: subscriptionUnitRows,
              },
            ],
          });
        }
        setPriceOptions(normalizedAll);
      } catch (err) {
        setError(err?.message || "Unable to load price options");
        setPriceOptions([]);
      } finally {
        setLoading(false);
      }
    };
    fetchPrices();
  }, [
    open,
    subscription,
    currentPriceId,
    currentUnitPriceType,
    currentVendorId,
    currentVendorName,
    subscriptionUnitRows,
  ]);

  const selectedPrice = useMemo(
    () =>
      allPriceOptions.find(
        (p) => Number(p.price_id) === Number(selectedPriceId),
      ) || null,
    [allPriceOptions, selectedPriceId],
  );

  const selectedVendorGroup = useMemo(
    () =>
      selectedPrice?.vendorGroups?.find(
        (g) => Number(g.vendor_id) === Number(selectedVendorId),
      ) || null,
    [selectedPrice, selectedVendorId],
  );

  useEffect(() => {
    if (!selectedPrice) {
      setSelectedVendorId("");
      setRenderedRows([]);
      return;
    }
    if (!selectedPrice.vendorGroups.length) {
      setSelectedVendorId("");
      setRenderedRows([]);
      return;
    }
    const isCurrentSelected =
      !currentPriceRemoved &&
      Number(selectedPrice.price_id) === Number(currentPriceId);
    if (
      isCurrentSelected &&
      Number(selectedVendorId) === Number(currentVendorId)
    )
      return;
    if (
      !selectedPrice.vendorGroups.some(
        (g) => Number(g.vendor_id) === Number(selectedVendorId),
      )
    ) {
      if (isCurrentSelected) {
        const exactCurrentVendor = selectedPrice.vendorGroups.find(
          (g) => Number(g.vendor_id) === Number(currentVendorId),
        );
        if (exactCurrentVendor) {
          setSelectedVendorId(String(exactCurrentVendor.vendor_id));
          return;
        }
        const exactGroupBySet = selectedPrice.vendorGroups.find((g) => {
          const key = normalizeUnits(g.unit_ids)
            .map((u) => u.id)
            .sort((a, b) => a - b)
            .join(",");
          return key === currentUnitSetKey;
        });
        if (exactGroupBySet) {
          setSelectedVendorId(String(exactGroupBySet.vendor_id));
          return;
        }
      }
      setSelectedVendorId(String(selectedPrice.vendorGroups[0].vendor_id));
    }
    // eslint-disable-next-line
  }, [
    selectedPriceId,
    selectedPrice,
    currentPriceId,
    currentPriceRemoved,
    currentUnitSetKey,
    selectedVendorId,
    currentVendorId,
  ]);

  useEffect(() => {
    if (!selectedVendorGroup || !quantity) {
      setRenderedRows([]);
      setQuantityRangeError("");
      return;
    }
    const isCurrentSelection =
      Number(selectedPrice?.price_id) === Number(currentPriceId) &&
      Number(selectedVendorGroup?.vendor_id) === Number(currentVendorId);
    const effectiveUnits = isCurrentSelection
      ? overlayMarketplaceFromSubscription(
          selectedVendorGroup.unit_ids || [],
          subscriptionUnitRows,
        )
      : normalizeUnits(selectedVendorGroup.unit_ids || []);
    const { min, max } = getUnitRangeBounds(effectiveUnits);
    if (!min || !max) {
      setRenderedRows([]);
      setQuantityRangeError("");
      return;
    }
    let safeQuantity = Number(quantity);
    if (safeQuantity < min) safeQuantity = min;
    if (safeQuantity > max) safeQuantity = max;
    if (safeQuantity !== Number(quantity)) {
      setQuantity(safeQuantity);
      setQuantityRangeError(
        `Quantity adjusted to ${safeQuantity} (allowed ${min}-${max}).`,
      );
      return;
    }
    setQuantityRangeError("");
    const rows = buildRowsByQuantity(
      effectiveUnits,
      selectedPrice?.unit_price_type,
      safeQuantity,
    );
    setRenderedRows(rows);
  }, [
    selectedVendorGroup,
    selectedPrice,
    quantity,
    currentPriceId,
    currentVendorId,
    subscriptionUnitRows,
  ]);

  const handleMarketplacePriceChange = (rowIndex, value) => {
    if (!/^\d*\.?\d*$/.test(value)) return;
    setRenderedRows((prev) =>
      prev.map((row, idx) => {
        if (idx !== rowIndex) return row;
        const price = Number(value || 0);
        return {
          ...row,
          marketplace_price: price,
          total: Number((row.quantity || 0) * price).toFixed(2),
        };
      }),
    );
  };

  const amount = useMemo(
    () => renderedRows.reduce((sum, row) => sum + Number(row.total || 0), 0),
    [renderedRows],
  );
  const isIndiaSubscription = Boolean(
    String(subscription?.currency || "").toUpperCase() === "INR" ||
      String(subscription?.region || "").toUpperCase() === "IN" ||
      String(subscription?.country_code || "").toUpperCase() === "IN",
  );
  const previewTaxPercent = 18;
  const previewTaxAmount = useMemo(
    () => Number((amount * (previewTaxPercent / 100)).toFixed(2)),
    [amount],
  );
  const previewTotalIncludingTax = useMemo(
    () => Number((amount + previewTaxAmount).toFixed(2)),
    [amount, previewTaxAmount],
  );

  // Catalog baseline for the selected vendor group, keyed by unit id. The
  // rendered rows start from these catalog prices; if any row's
  // marketplace_price no longer matches, the admin has set a CUSTOM price.
  const catalogPriceByUnitId = useMemo(() => {
    const map = new Map();
    normalizeUnits(selectedVendorGroup?.unit_ids || []).forEach((u) => {
      map.set(Number(u.id), Number(u.marketplace_price || 0));
    });
    return map;
  }, [selectedVendorGroup]);

  // A custom price = any rendered row whose marketplace_price differs from the
  // catalog price for that unit. Covers both an admin editing a row away from
  // catalog AND a sub that already carries an off-catalog (overlaid) price.
  const hasCustomPrice = useMemo(() => {
    if (!renderedRows.length || !catalogPriceByUnitId.size) return false;
    return renderedRows.some((row) => {
      const catalog = catalogPriceByUnitId.get(Number(row.id));
      if (catalog === undefined) return false;
      return Number(row.marketplace_price || 0) !== Number(catalog);
    });
  }, [renderedRows, catalogPriceByUnitId]);

  // Auto-default the pin checkbox to follow custom-price detection until the
  // admin manually toggles it, at which point their choice wins.
  useEffect(() => {
    if (!pinTouched) setPin(hasCustomPrice);
  }, [hasCustomPrice, pinTouched]);

  const handleUnpin = async () => {
    setError("");
    setSuccess("");
    setUnpinning(true);
    try {
      const sid = Number(getVal(subscription, ["id"], 0));
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/subscriptions/${sid}/unpin`,
        { method: "POST", credentials: "include" },
      );
      const data = await res.json();
      if (!res.ok || !(data?.success ?? data?.status === 200)) {
        throw new Error(data?.message || data?.msg || "Failed to unpin price");
      }
      setIsPinned(false);
      setSuccess("Price unpinned — future catalog updates will apply.");
      if (typeof onUpdated === "function") onUpdated(data);
    } catch (err) {
      setError(err?.message || "Failed to unpin price");
    } finally {
      setUnpinning(false);
    }
  };

  const buildPayload = () => {
    if (!selectedPrice || !selectedVendorGroup) {
      throw new Error("Select a target price and vendor group.");
    }
    if (
      currentPriceRemoved &&
      Number(selectedPrice.price_id) === Number(currentPriceId)
    ) {
      throw new Error("Migration to same price is not allowed.");
    }
    const selectedUnitSetKey = normalizeUnits(selectedVendorGroup.unit_ids)
      .map((u) => u.id)
      .sort((a, b) => a - b)
      .join(",");
    if (
      currentPriceRemoved &&
      selectedUnitSetKey &&
      selectedUnitSetKey === currentUnitSetKey
    ) {
      throw new Error("Migration to same unit set is not allowed.");
    }
    if (!renderedRows.length) {
      throw new Error("No applicable unit rows found for selected quantity.");
    }
    const currentEffectiveRows = buildRowsByQuantity(
      overlayMarketplaceFromSubscription(
        subscriptionUnitRows,
        subscriptionUnitRows,
      ),
      currentUnitPriceType,
      currentQuantity,
    );
    const noChanges =
      Number(selectedPrice.price_id) === Number(currentPriceId) &&
      Number(selectedVendorGroup.vendor_id) === Number(currentVendorId) &&
      Number(quantity) === Number(currentQuantity) &&
      JSON.stringify(buildUnitSignature(renderedRows)) ===
        JSON.stringify(buildUnitSignature(currentEffectiveRows));
    if (noChanges) {
      throw new Error("No changes detected. Update is not allowed.");
    }
    return {
      id: subscription.id,
      new_price_id: Number(selectedPrice.price_id),
      vendor_id: Number(selectedVendorGroup.vendor_id),
      quantity: Number(quantity),
      unit_ids: renderedRows,
      ...(couponInput.trim() ? { coupon: couponInput.trim() } : {}),
      pin: Boolean(pin),
      ...(pinNote.trim() ? { price_pin_note: pinNote.trim() } : {}),
    };
  };

  const checkCoupon = async () => {
    const code = couponInput.trim();
    setCouponInfo(null);
    setCouponError("");
    if (!code) return;
    setCouponChecking(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/check_coupon`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            coupon: code,
            region: getVal(subscription, ["regionId", "region"], 1),
          }),
        },
      );
      const j = await res.json();
      if (j?.status === 200 && j?.data?.valid) {
        setCouponInfo(j.data);
      } else {
        setCouponError("Coupon is not valid for this region.");
      }
    } catch (_) {
      setCouponError("Could not validate coupon.");
    } finally {
      setCouponChecking(false);
    }
  };

  // The vendor-cost boxes the admin has filled in, as the endpoint wants them.
  const vendorUpdatesFromEdits = () =>
    Object.entries(vendorPriceEdits)
      .filter(([, v]) => v !== "" && v !== undefined && v !== null)
      .map(([unit_id, v]) => ({
        unit_id: Number(unit_id),
        vendor_price: Number(v),
      }))
      .filter(
        (u) =>
          Number.isFinite(u.vendor_price) &&
          u.vendor_price >= 0 &&
          Number.isInteger(u.unit_id),
      );

  const postVendorPrices = async (updates) => {
    const subId = Number(getVal(subscription, ["id"], 0));
    const res = await fetch(
      `${process.env.REACT_APP_SERVER_URL}/admin/subscriptions/${subId}/vendor_prices`,
      {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ updates }),
      },
    );
    const data = await res.json();
    if (!data?.success) throw new Error(data?.msg || "Failed to update vendor prices");
    return data;
  };

  // ONE SAVE FOR THE WHOLE DRAWER. Save Update used to ignore the vendor-cost
  // boxes, so changing both meant save the cost, close, reopen, then save the
  // price. Now the rows Save Update writes carry the edited cost — matched by
  // tier id, or by the same band when the update moves to another tier set —
  // and every row nobody edited keeps the cost it already had.
  const withVendorEdits = (rows, updates) => {
    if (!updates.length) return rows;
    const byId = new Map(updates.map((u) => [u.unit_id, u.vendor_price]));
    const subById = new Map(subscriptionUnitRows.map((u) => [Number(u.id), u]));
    return (Array.isArray(rows) ? rows : []).map((row) => {
      if (byId.has(Number(row.id))) {
        return { ...row, vendor_price: byId.get(Number(row.id)) };
      }
      const sameBand = updates.find((u) => {
        const s = subById.get(u.unit_id);
        return (
          s &&
          Number(s.start_range) === Number(row.start_range) &&
          Number(s.end_range) === Number(row.end_range)
        );
      });
      return sameBand ? { ...row, vendor_price: sameBand.vendor_price } : row;
    });
  };

  const handleSave = async () => {
    setError("");
    setSuccess("");
    try {
      const payload = buildPayload();
      setPendingPayload(payload);
      setApplyWhen("immediate");
      setShowApplyDialog(true);
    } catch (err) {
      // Only the vendor cost changed: that is a save, not "no changes".
      if (
        vendorUpdatesFromEdits().length &&
        /no changes detected/i.test(err?.message || "")
      ) {
        await handleSaveVendorPrices();
        return;
      }
      setError(err?.message || "Unable to continue.");
    }
  };

  const handleConfirmUpdate = async () => {
    if (!pendingPayload) return;
    // Validate the custom date client-side before round-tripping.
    if (applyWhen === "custom_date") {
      if (!customDate) {
        setError("Pick a date for the scheduled update.");
        return;
      }
      const picked = new Date(`${customDate}T00:00:00`);
      if (Number.isNaN(picked.getTime()) || picked <= new Date()) {
        setError("Custom date must be in the future.");
        return;
      }
    }
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      // Edited vendor costs go in with the update: onto the subscription now
      // (a deferred update must not hold back an internal cost), and onto the
      // rows the update writes, so applying it cannot put the old cost back.
      const vendorUpdates = vendorUpdatesFromEdits();
      if (vendorUpdates.length) await postVendorPrices(vendorUpdates);
      const requestBody = {
        apply_when: applyWhen,
        payload: {
          ...pendingPayload,
          unit_ids: withVendorEdits(pendingPayload.unit_ids, vendorUpdates),
          ...(applyWhen === "custom_date" ? { custom_date: customDate } : {}),
        },
      };
      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/subscriptions/update`,
        {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(requestBody),
        },
      );
      const data = await response.json();
      if (!response.ok || !data?.success) {
        throw new Error(data?.message || "Failed to update subscription");
      }
      // A seat change on an Ingram Microsoft subscription is held until Ingram
      // confirms — "updated successfully" would claim a Stripe change that has
      // not happened yet.
      if (data?.data?.vendor_pending) {
        setSuccess(data.data.message || "Seat change sent to Ingram. Stripe will update once Microsoft confirms.");
        setPendingSeatChanges((prev) => [
          {
            id: data.data.seat_change_id,
            status: data.data.status,
            from_qty: data.data.from_qty,
            to_qty: data.data.to_qty,
            effective_at: data.data.effective_at,
            fire_at: data.data.fire_at,
            order_id: data.data.order_id,
          },
          ...prev,
        ]);
      } else {
        setSuccess(
          vendorUpdates.length
            ? "Subscription and vendor cost updated."
            : "Subscription updated successfully.",
        );
      }
      if (vendorUpdates.length) setVendorPriceEdits({});
      setShowApplyDialog(false);
      if (typeof onUpdated === "function") onUpdated(data);
    } catch (err) {
      setError(err?.message || "Failed to update subscription");
    } finally {
      setSaving(false);
    }
  };

  // Vendor-cost save — independent of the main Save Update flow.
  // Calls /admin/subscriptions/:id/vendor_prices which mutates the
  // unit_ids JSON only. No Stripe, no schedule, no pending patch.
  const handleSaveVendorPrices = async () => {
    setVendorPriceError("");
    setVendorPriceSuccess("");
    const updates = vendorUpdatesFromEdits();
    if (!updates.length) {
      setVendorPriceError("Edit at least one vendor price before saving.");
      return;
    }
    setSavingVendorPrices(true);
    try {
      const data = await postVendorPrices(updates).catch((e) => ({
        success: false,
        msg: e.message,
      }));
      if (data?.success) {
        const notFound = (data.data?.not_found_unit_ids || []).length;
        const changed = Number(data.data?.updated || 0);
        // A save where the submitted price already matched still counts
        // as a review — that is the normal outcome of the rate-cleanup
        // pass. Saying "Updated 0 units" made it look like a no-op.
        const headline = changed
          ? `Updated ${changed} unit${changed === 1 ? "" : "s"}.`
          : data.data?.confirmed
            ? "Rate confirmed — no change needed."
            : "Nothing to update.";
        setVendorPriceSuccess(
          `${headline} No Stripe call.${notFound ? ` (${notFound} unit_id${notFound === 1 ? "" : "s"} not found on this sub.)` : ""}`,
        );
        setVendorPriceEdits({});
        if (typeof onUpdated === "function") onUpdated(data);
      } else {
        setVendorPriceError(data?.msg || "Failed to update vendor prices");
      }
    } catch (err) {
      setVendorPriceError(err?.message || "Failed to update vendor prices");
    } finally {
      setSavingVendorPrices(false);
    }
  };

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: { xs: "100vw", md: 620 }, p: 2.5 }}>
        <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 1.5 }}>
          Update Subscription
        </Typography>
        <Typography sx={{ fontSize: 14, color: "#475467", mb: 2 }}>
          Choose a new price and vendor unit group for migration.
        </Typography>

        {isPinned && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 2,
              border: "1px solid #c7d2fe",
              bgcolor: "#eef2ff",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1,
            }}
          >
            <Box>
              <Chip
                label="Pinned"
                size="small"
                color="primary"
                sx={{ fontWeight: 700, mb: 0.5 }}
              />
              <Typography sx={{ fontSize: 12.5, color: "#3730a3" }}>
                This price is pinned and is excluded from catalog price updates.
                {(() => {
                  const note = getVal(
                    subscription,
                    ["price_pin_note", "pricePinNote"],
                    "",
                  );
                  return note ? ` Note: ${note}` : "";
                })()}
              </Typography>
            </Box>
            <Button
              size="small"
              color="primary"
              variant="outlined"
              disabled={unpinning}
              sx={{ textTransform: "none", whiteSpace: "nowrap" }}
              onClick={handleUnpin}
            >
              {unpinning ? "Unpinning…" : "Unpin"}
            </Button>
          </Box>
        )}

        {pendingSeatChanges.length > 0 && (
          <Box
            sx={{
              mb: 2,
              p: 1.75,
              borderRadius: 2,
              border: "1px solid #93c5fd",
              bgcolor: "#eff6ff",
            }}
          >
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#1e3a8a", mb: 0.5 }}>
              Seat change waiting on Ingram
            </Typography>
            {pendingSeatChanges.map((c) => {
              const label = {
                scheduled: `scheduled — placed on Ingram ${c.fire_at ? new Date(c.fire_at).toLocaleDateString() : ""}`,
                firing: "being placed on Ingram",
                vendor_pending: `placed on Ingram${c.order_id ? ` (Order_${c.order_id})` : ""} — waiting for Microsoft to confirm`,
                applying: "confirmed by Ingram — updating Stripe",
              }[c.status] || c.status;
              return (
                <Typography key={c.id} sx={{ fontSize: 12.5, color: "#1e3a8a", lineHeight: 1.7 }}>
                  {c.from_qty} → {c.to_qty} seats · {label}
                  {c.effective_at ? ` · takes effect ${new Date(c.effective_at).toLocaleDateString()}` : ""}
                </Typography>
              );
            })}
            <Typography sx={{ fontSize: 12, color: "#1e40af", mt: 0.5 }}>
              Stripe is not changed until Microsoft confirms the seats. If Ingram refuses, nothing is charged and admins are emailed.
            </Typography>
          </Box>
        )}

        {schedule?.hasSchedule && schedule.upcoming?.[0] && (
          <Box
            sx={{
              mb: 2,
              p: 1.75,
              borderRadius: 2,
              border: "1px solid #fcd34d",
              bgcolor: "#fffbeb",
            }}
          >
            <Typography
              sx={{ fontSize: 13, fontWeight: 700, color: "#92400e", mb: 0.5 }}
            >
              ⚠ A change is already scheduled on Stripe
            </Typography>
            {(() => {
              const ph = schedule.upcoming[0];
              const it = ph.items?.[0] || {};
              const sym = ph.currency === "INR" ? "₹" : "$";
              const fmt = (n) => `${sym}${Number(n || 0).toLocaleString()}`;
              return (
                <Typography
                  sx={{ fontSize: 12.5, color: "#78350f", lineHeight: 1.7 }}
                >
                  Effective {new Date(ph.effectiveDate).toLocaleDateString()} ·{" "}
                  {fmt(it.unitAmount)}/unit × {it.quantity}
                  <br />
                  Subtotal {fmt(ph.subtotal)}
                  {ph.discount && ph.discountAmount > 0 && (
                    <>
                      {" "}
                      · {ph.discount.name}
                      {ph.discount.percentOff != null
                        ? ` (${ph.discount.percentOff}% off)`
                        : ""}{" "}
                      −{fmt(ph.discountAmount)}
                    </>
                  )}
                  <br />
                  GST {ph.taxPct}% {fmt(ph.taxAmount)} ·{" "}
                  <b>Amount due {fmt(ph.amountDue)}</b>
                  <br />
                  Saving a new update will <b>replace</b> this scheduled change.
                </Typography>
              );
            })()}
            <Button
              size="small"
              color="warning"
              variant="outlined"
              disabled={cancelingSchedule}
              sx={{ mt: 1, textTransform: "none" }}
              onClick={async () => {
                setCancelingSchedule(true);
                try {
                  const sid = Number(getVal(subscription, ["id"], 0));
                  await fetch(
                    `${process.env.REACT_APP_SERVER_URL}/admin/subscriptions/${sid}/schedule/cancel`,
                    { method: "POST", credentials: "include" },
                  );
                  setSchedule(null);
                } catch (_) {
                  /* non-fatal */
                } finally {
                  setCancelingSchedule(false);
                }
              }}
            >
              {cancelingSchedule ? "Cancelling…" : "Cancel scheduled change"}
            </Button>
          </Box>
        )}

        {loading && (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <CircularProgress size={26} />
          </Box>
        )}

        {!loading && (
          <>
            <FormControl fullWidth sx={{ mb: 1.5 }}>
              <InputLabel id="target-price-label">Target Price</InputLabel>
              <Select
                labelId="target-price-label"
                value={selectedPriceId}
                label="Target Price"
                onChange={(e) => setSelectedPriceId(e.target.value)}
                disabled={!currentPriceRemoved}
              >
                {allPriceOptions.map((price) => (
                  <MenuItem key={price.price_id} value={String(price.price_id)}>
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                        {price.price_name}
                      </Typography>
                      <Typography sx={{ fontSize: 12, color: "#667085" }}>
                        Type: {price.type_name || "-"} | Interval:{" "}
                        {price.interval_label || "-"}
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth sx={{ mb: 1.5 }} disabled={!selectedPrice}>
              <InputLabel id="vendor-group-label">Vendor Unit Group</InputLabel>
              <Select
                labelId="vendor-group-label"
                value={selectedVendorId}
                label="Vendor Unit Group"
                onChange={(e) => setSelectedVendorId(e.target.value)}
              >
                {(selectedPrice?.vendorGroups || []).map((group) => (
                  <MenuItem
                    key={group.vendor_id}
                    value={String(group.vendor_id)}
                  >
                    {group.vendor_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              type="number"
              label="Quantity"
              value={quantity}
              onChange={(e) => setQuantity(Number(e.target.value || 0))}
              sx={{ mb: 2 }}
              inputProps={{ min: 1 }}
            />
            {quantityRangeError && (
              <Alert severity="warning" sx={{ mb: 1.5 }}>
                {quantityRangeError}
              </Alert>
            )}

            <Box sx={{ mb: 1, display: "flex", gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setCurrentPriceRemoved(true);
                  setSelectedPriceId("");
                  setSelectedVendorId("");
                  setRenderedRows([]);
                }}
                disabled={currentPriceRemoved}
              >
                Delete Current Price
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={() => {
                  setCurrentPriceRemoved(false);
                  setSelectedPriceId(String(currentPriceId || ""));
                  setSelectedVendorId(String(currentVendorId || ""));
                  setError("");
                }}
                disabled={!currentPriceRemoved}
              >
                Restore Current Price
              </Button>
            </Box>

            {renderedRows.map((row, idx) => (
              <Box
                key={`${row.id}-${idx}`}
                sx={{
                  border: "1px solid #E4E7EC",
                  borderRadius: "10px",
                  p: 1.5,
                  mb: 1,
                  background: "#F8FAFC",
                }}
              >
                <Typography sx={{ fontSize: 13, color: "#475467", mb: 0.5 }}>
                  Range: {row.start_range} - {row.end_range} | Qty:{" "}
                  {row.quantity}
                </Typography>
                <TextField
                  fullWidth
                  label="Marketplace Price"
                  type="number"
                  value={row.marketplace_price}
                  onChange={(e) =>
                    handleMarketplacePriceChange(idx, e.target.value)
                  }
                  sx={{ mb: 1 }}
                />
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                  Total: {row.total}
                </Typography>
              </Box>
            ))}

            <Box
              sx={{
                borderTop: "1px solid #E4E7EC",
                mt: 2,
                pt: 1.5,
                display: "flex",
                flexDirection: "column",
                gap: 0.5,
              }}
            >
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontWeight: 600 }}>Amount</Typography>
                <Typography sx={{ fontWeight: 700 }}>
                  {amount.toFixed(2)}
                </Typography>
              </Box>
              {isIndiaSubscription && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 600 }}>
                    Tax (GST {previewTaxPercent}%)
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {previewTaxAmount.toFixed(2)}
                  </Typography>
                </Box>
              )}
              {isIndiaSubscription && (
                <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                  <Typography sx={{ fontWeight: 700 }}>
                    Total Including Tax
                  </Typography>
                  <Typography sx={{ fontWeight: 700 }}>
                    {previewTotalIncludingTax.toFixed(2)}
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Coupon (optional) — validated via /check_coupon, applied on save. */}
            <Box
              sx={{ mt: 2, p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}
            >
              <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1 }}>
                Coupon (optional)
              </Typography>
              {/* A price update no longer inherits the old coupon — a
                  discount agreed against the previous rate must not
                  follow the customer onto a renegotiated one by
                  default. Say so plainly, with the code to hand, so
                  keeping it is one copy-paste away. */}
              {currentDiscount && !couponInput.trim() ? (
                <Box
                  sx={{
                    mb: 1,
                    p: 1,
                    borderRadius: 1.5,
                    background: "#fff7ed",
                    border: "1px solid #fed7aa",
                  }}
                >
                  <Typography sx={{ fontSize: 12.5, color: "#9a3412" }}>
                    This subscription currently has{" "}
                    <strong>{currentDiscount.name}</strong>
                    {currentDiscount.percentOff != null
                      ? ` (${currentDiscount.percentOff}% off)`
                      : currentDiscount.amountOff != null
                        ? ` (${currentDiscount.amountOff.toLocaleString()} off)`
                        : ""}
                    . Saving will <strong>remove it</strong> — the new
                    price is charged in full.
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => {
                      setCouponInput(currentDiscount.id);
                      setCouponInfo(null);
                      setCouponError("");
                    }}
                    sx={{
                      textTransform: "none",
                      fontSize: 12,
                      p: 0,
                      minWidth: 0,
                      mt: 0.25,
                    }}
                  >
                    Keep it — use {currentDiscount.id}
                  </Button>
                </Box>
              ) : null}
              <Box sx={{ display: "flex", gap: 1 }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Stripe coupon code"
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value);
                    setCouponInfo(null);
                    setCouponError("");
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={checkCoupon}
                  disabled={couponChecking || !couponInput.trim()}
                  sx={{ textTransform: "none", whiteSpace: "nowrap" }}
                >
                  {couponChecking ? "…" : "Check"}
                </Button>
              </Box>
              {couponInfo && (
                <Typography sx={{ fontSize: 12.5, color: "#15803d", mt: 0.75 }}>
                  ✓ {couponInfo.name || couponInput}
                  {couponInfo.percent_off != null
                    ? ` — ${couponInfo.percent_off}% off`
                    : couponInfo.amount_off != null
                      ? ` — ${(couponInfo.amount_off / 100).toLocaleString()} off`
                      : ""}
                </Typography>
              )}
              {couponError && (
                <Typography sx={{ fontSize: 12.5, color: "#b91c1c", mt: 0.75 }}>
                  {couponError}
                </Typography>
              )}
              <Typography sx={{ fontSize: 11.5, color: "#94a3b8", mt: 0.5 }}>
                Applied on save — immediately, or on the scheduled phase.
                Left empty, the subscription ends up with no coupon.
              </Typography>
            </Box>

            {/* Pin this price — excludes the sub from catalog price rollouts.
                Auto-checks when a custom (off-catalog) price is detected. */}
            <Box
              sx={{ mt: 2, p: 1.5, borderRadius: 2, border: "1px solid #e2e8f0" }}
            >
              <FormControlLabel
                sx={{ alignItems: "flex-start", m: 0 }}
                control={
                  <Checkbox
                    checked={pin}
                    onChange={(e) => {
                      setPin(e.target.checked);
                      setPinTouched(true);
                    }}
                    size="small"
                    sx={{ pt: 0 }}
                  />
                }
                label={
                  <Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                      Pin this price (exclude from catalog price updates)
                    </Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#94a3b8" }}>
                      {hasCustomPrice
                        ? "Custom price detected — auto-pinned so catalog rollouts won't overwrite it."
                        : "Keeps this subscription's price locked when the catalog price changes."}
                    </Typography>
                  </Box>
                }
              />
              {pin && (
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Pin note (optional) — why is this price locked?"
                  value={pinNote}
                  onChange={(e) => setPinNote(e.target.value)}
                  sx={{ mt: 1 }}
                />
              )}
            </Box>

            {error && (
              <Alert severity="error" sx={{ mt: 1.5 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mt: 1.5 }}>
                {success}
              </Alert>
            )}

            {/* Vendor cost editor. Its own button saves only the cost
                (purchasesubscriptions.unit_ids[i].vendor_price — no Stripe
                call, no schedule update); Save Update saves it as well. */}
            {subscriptionUnitRows.length > 0 && (
              <Box
                sx={{
                  mt: 3,
                  p: 1.75,
                  borderRadius: 2,
                  border: "1px dashed #cfd9e6",
                  background: "#f7fbff",
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ fontWeight: 700, color: "#1f2c3c" }}
                  >
                    Vendor cost (internal — no Stripe sync)
                  </Typography>
                  <Typography variant="caption" sx={{ color: "#607084" }}>
                    Edit what we PAY the vendor per unit. Customer billing
                    (marketplace_price) is untouched. Save Update saves these
                    too; Save vendor cost saves only them.
                  </Typography>
                </Box>
                {subscriptionUnitRows.map((u) => {
                  const editedVal =
                    vendorPriceEdits[u.id] !== undefined
                      ? vendorPriceEdits[u.id]
                      : "";
                  const currentVendorPrice = Number(
                    getVal(u, ["vendor_price", "Vendor_price"], 0),
                  );
                  return (
                    <Box
                      key={u.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.25,
                        py: 0.75,
                        borderTop: "1px solid #e6eff7",
                      }}
                    >
                      <Typography
                        sx={{
                          minWidth: 140,
                          fontSize: 13,
                          color: "#3d4a5c",
                          fontWeight: 600,
                        }}
                      >
                        Unit #{u.id}
                        <Box
                          component="span"
                          sx={{ color: "#8696aa", fontWeight: 400, ml: 0.5 }}
                        >
                          ({u.start_range}–{u.end_range})
                        </Box>
                      </Typography>
                      <Typography
                        sx={{ minWidth: 120, fontSize: 12, color: "#8696aa" }}
                      >
                        Mkt: {Number(u.marketplace_price || 0).toFixed(2)} ·
                        Current: {currentVendorPrice.toFixed(2)}
                      </Typography>
                      <TextField
                        size="small"
                        type="number"
                        placeholder={String(currentVendorPrice)}
                        value={editedVal}
                        onChange={(e) =>
                          setVendorPriceEdits((prev) => ({
                            ...prev,
                            [u.id]: e.target.value,
                          }))
                        }
                        inputProps={{ min: 0, step: "0.01" }}
                        sx={{ flex: 1 }}
                        disabled={savingVendorPrices}
                      />
                    </Box>
                  );
                })}
                {vendorPriceError && (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {vendorPriceError}
                  </Alert>
                )}
                {vendorPriceSuccess && (
                  <Alert severity="success" sx={{ mt: 1 }}>
                    {vendorPriceSuccess}
                  </Alert>
                )}
                <Box
                  sx={{ mt: 1.25, display: "flex", justifyContent: "flex-end" }}
                >
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleSaveVendorPrices}
                    disabled={
                      savingVendorPrices ||
                      Object.values(vendorPriceEdits).every(
                        (v) => v === "" || v === undefined || v === null,
                      )
                    }
                  >
                    {savingVendorPrices ? "Saving..." : "Save vendor cost"}
                  </Button>
                </Box>
              </Box>
            )}

            <Box
              sx={{
                mt: 2,
                display: "flex",
                gap: 1,
                justifyContent: "flex-end",
              }}
            >
              <Button onClick={onClose} disabled={saving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || !selectedPriceId || !selectedVendorId}
              >
                {saving ? "Saving..." : "Save Update"}
              </Button>
            </Box>
          </>
        )}
      </Box>
      <Dialog
        open={showApplyDialog}
        onClose={() => !saving && setShowApplyDialog(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>When should these changes apply?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            This subscription update has highest priority. It will bypass all
            pending and upcoming price updates for this subscription. Please
            review carefully before continuing.
          </Alert>
          <RadioGroup
            value={applyWhen}
            onChange={(e) => setApplyWhen(e.target.value)}
          >
            <FormControlLabel
              value="immediate"
              control={<Radio />}
              label="Immediately (prorate — charges or credits the rest of this period)"
            />
            <FormControlLabel
              value="immediate_no_proration"
              control={<Radio />}
              label="Immediately, without proration (no charge, no credit)"
            />
            <FormControlLabel
              value="end_of_current_period"
              control={<Radio />}
              label={`End of current renewal period (${dueDateLabel})`}
            />
            <FormControlLabel
              value="custom_date"
              control={<Radio />}
              label="On a custom date"
            />
          </RadioGroup>
          {applyWhen === "immediate_no_proration" && (
            <Alert severity="info" sx={{ mt: 1, mb: 1 }}>
              The new price and units go to Stripe now, but this period is left
              alone — no proration invoice and no credit note. The new price
              first bills on the next invoice. Use this to correct a price (and
              pin it) without touching the customer's money.
            </Alert>
          )}
          {applyWhen === "custom_date" && (
            <Box sx={{ mt: 1, ml: 4, mb: 1 }}>
              <TextField
                type="date"
                size="small"
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                inputProps={{
                  // Stop the picker from accepting today / past dates —
                  // the BE rejects anything <= now anyway.
                  min: new Date(Date.now() + 86400000)
                    .toISOString()
                    .slice(0, 10),
                }}
                fullWidth
              />
              <Typography
                variant="caption"
                sx={{ display: "block", mt: 0.5, color: "text.secondary" }}
              >
                Schedule will apply on this date at 00:00 UTC. The customer's
                current period extends until then; then the new price kicks in.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowApplyDialog(false)} disabled={saving}>
            Close
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmUpdate}
            disabled={saving}
          >
            {saving ? "Updating..." : "Update Subscription"}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  );
};

export default AdminUpdateSubscriptionDrawer;
